import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, Clock } from 'lucide-react';

interface RouteTest {
  path: string;
  name: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
}

const NavHealthCheck: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isRunning, setIsRunning] = useState(false);
  const [tests, setTests] = useState<RouteTest[]>([
    { path: '/', name: 'Home Page', status: 'pending' },
    { path: '/login', name: 'Login Page', status: 'pending' },
    { path: '/register', name: 'Register Page', status: 'pending' },
    { path: '/dashboard', name: 'Dashboard (Protected)', status: 'pending' },
    { path: '/events', name: 'Events Page', status: 'pending' },
    { path: '/groups', name: 'Groups Page', status: 'pending' },
    { path: '/profile', name: 'Profile Page', status: 'pending' },
  ]);

  const runHealthCheck = async () => {
    setIsRunning(true);
    
    for (let i = 0; i < tests.length; i++) {
      const test = tests[i];
      if (!test) continue;
      
      setTests(prevTests => 
        prevTests.map((t, index) => 
          index === i ? { ...t, status: 'pending' } : t
        )
      );

      try {
        // Test navigation
        navigate(test.path);
        
        // Wait a bit for navigation to complete
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Check if we're on the expected path (or redirected for protected routes)
        const currentPath = window.location.pathname;
        const isProtected = test?.name.includes('Protected') || false;
        
        if (isProtected && currentPath === '/login') {
          // Protected route correctly redirected to login
          setTests(prevTests => 
            prevTests.map((t, index) => 
              index === i ? { ...t, status: 'success' } : t
            )
          );
        } else if (!isProtected && currentPath === test?.path) {
          // Public route accessible
          setTests(prevTests => 
            prevTests.map((t, index) => 
              index === i ? { ...t, status: 'success' } : t
            )
          );
        } else {
          setTests(prevTests => 
            prevTests.map((t, index) => 
              index === i ? { 
                ...t, 
                status: 'error', 
                error: `Expected ${test?.path || 'unknown'}, got ${currentPath}` 
              } : t
            )
          );
        }
      } catch (error) {
        setTests(prevTests => 
          prevTests.map((t, index) => 
            index === i ? { 
              ...t, 
              status: 'error', 
              error: error instanceof Error ? error.message : 'Unknown error' 
            } : t
          )
        );
      }
    }
    
    setIsRunning(false);
  };

  const getStatusIcon = (status: RouteTest['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: RouteTest['status']) => {
    switch (status) {
      case 'success':
        return 'text-green-700 bg-green-50';
      case 'error':
        return 'text-red-700 bg-red-50';
      default:
        return 'text-gray-700 bg-gray-50';
    }
  };

  const successCount = tests.filter(t => t.status === 'success').length;
  const errorCount = tests.filter(t => t.status === 'error').length;

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Navigation Health Check
          </h2>
          <button
            onClick={runHealthCheck}
            disabled={isRunning}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRunning ? 'Running...' : 'Run Tests'}
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center p-3 bg-gray-50 rounded-xl">
            <div className="text-2xl font-bold text-gray-900">{tests.length}</div>
            <div className="text-sm text-gray-600">Total Routes</div>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-xl">
            <div className="text-2xl font-bold text-green-600">{successCount}</div>
            <div className="text-sm text-green-600">Passed</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-xl">
            <div className="text-2xl font-bold text-red-600">{errorCount}</div>
            <div className="text-sm text-red-600">Failed</div>
          </div>
        </div>

        {/* Test Results */}
        <div className="space-y-2">
          {tests.map((test, index) => (
            <div
              key={index}
              className={`flex items-center justify-between p-3 rounded-xl ${getStatusColor(test.status)}`}
            >
              <div className="flex items-center space-x-3">
                {getStatusIcon(test.status)}
                <span className="font-medium">{test.name}</span>
                <span className="text-sm opacity-75">({test.path})</span>
              </div>
              {test.error && (
                <span className="text-sm text-red-600">{test.error}</span>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm text-blue-700">
            <strong>Current Location:</strong> {location.pathname}
          </p>
          <p className="text-sm text-blue-600 mt-1">
            This tool tests basic navigation functionality. Protected routes should redirect to /login when not authenticated.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NavHealthCheck;